import has from 'lodash/has.js';
import merge from 'lodash/merge.js';

import Collection from '@/store/quill/Collection.js';
import BaseGradingScheme from './BaseGradingScheme.js';

export default function ({ name }) {
    return merge(Collection(name, BaseGradingScheme), {
        state: {},

        getters: {
            getField:
                state =>
                ({ uuid, index, field }) =>
                    state[uuid].criterias[index][field],

            getCriterias:
                state =>
                ({ uuid }) => {
                    if (!has(state, uuid)) return [];

                    return state[uuid].criterias;
                },
        },

        actions: {
            async addCriteria(context, { uuid, type, criteria }) {
                if (!has(context.state, uuid)) {
                    const isCourseType = type === 'course' || type === 'course_template';
                    const isActivityType = type === 'activity' || type === 'activity_template';

                    if (isCourseType) {
                        await context.dispatch('add', {
                            uuid,
                            course_id: uuid,
                        });
                    }

                    if (isActivityType) {
                        await context.dispatch('add', {
                            uuid,
                            activity_uuid: uuid,
                        });
                    }
                }

                const index = context.state[uuid].criterias.length;

                await context.commit('ADD_CRITERIA', {
                    uuid,
                    criteria: { is_minimum_passing: false, ...criteria, index },
                });

                context.commit('REORDER', { uuid, field: 'reorder', type });
            },

            hydrate(context, { grading_scheme }) {
                // set index since it's not stored in the database
                grading_scheme.criterias.forEach((item, index) => {
                    item.index = index;
                    item.minimum *= 100;
                });

                context.dispatch('add', { ...grading_scheme });
            },

            removeCriteria(context, { uuid, index, type }) {
                context.commit('REMOVE', { uuid, index });

                context.commit('REORDER', { uuid, field: 'reorder', type });
            },

            setField(context, { uuid, index, field, value }) {
                context.commit('EDIT_CRITERIA_FIELD', {
                    uuid,
                    index,
                    field,
                    value,
                });
            },

            updateSchemeIds(context, { scheme, uuid }) {
                context.commit('EDIT_FIELD', {
                    uuid,
                    field: 'id',
                    value: scheme.id,
                });

                // If the backend did not return criterias (e.g., empty template), stop here
                if (!scheme || !Array.isArray(scheme.criterias)) {
                    return;
                }

                // Match criterias by name and minimum value instead of index
                // to handle cases where frontend and backend order might differ
                scheme.criterias.forEach(serverCriteria => {
                    const frontendCriteriaIndex = context.state[uuid].criterias.findIndex(
                        frontendCriteria =>
                            frontendCriteria.name === serverCriteria.name &&
                            Math.abs(Number(frontendCriteria.minimum) - Number(serverCriteria.minimum * 100)) < 0.01
                    );

                    if (frontendCriteriaIndex !== -1) {
                        context.commit('EDIT_CRITERIA_FIELD', {
                            uuid,
                            index: frontendCriteriaIndex,
                            field: 'id',
                            value: serverCriteria.id,
                        });
                    }
                });
            },

            removeAllCriterias(context, { uuid }) {
                context.commit('REMOVE_ALL_CRITERIAS', { uuid });
            },
        },

        mutations: {
            EDIT_FIELD(state, { uuid, field, value }) {
                state[uuid][field] = value;
            },

            ADD_CRITERIA(state, payload) {
                // Create a new array reference to ensure Vue reactivity
                state[payload.uuid].criterias = [...state[payload.uuid].criterias, payload.criteria];
            },

            EDIT_CRITERIA_FIELD(state, { uuid, index, field, value }) {
                // Create a new array reference to ensure Vue reactivity
                const criterias = [...state[uuid].criterias];
                criterias[index][field] = value;
                state[uuid].criterias = criterias;
            },

            REORDER(state, { uuid }) {
                const { criterias } = state[uuid];

                if (criterias.length === 1) {
                    // Create a new array reference to ensure Vue reactivity
                    const updatedCriterias = [...criterias];
                    updatedCriterias[0].index = 0;
                    state[uuid].criterias = updatedCriterias;
                    return;
                }

                criterias.sort((x, y) => Number(y.minimum) - Number(x.minimum));

                criterias.forEach((criteria, index) => {
                    criteria.index = index;
                });

                // Reassign to a new array reference to guarantee reactivity updates
                state[uuid].criterias = [...criterias];
            },

            REMOVE(state, { uuid, index }) {
                // Create a new array reference to ensure Vue reactivity
                const criterias = [...state[uuid].criterias];
                criterias.splice(index, 1);
                state[uuid].criterias = criterias;
            },

            REMOVE_ALL_CRITERIAS(state, { uuid }) {
                state[uuid].criterias = [];
            },
        },
    });
}
